import React from "react";
import Link from "gatsby-link";
import { rgba, shade } from "polished";
import styled, { ThemeProvider } from "styled-components";
import { Box, Flex } from "rebass";
import Text from "./Text";
import H2 from "./Headings/H2";
import Button from "./Buttons/Button";
import OverlayImage from "./OverlayImage";
import theme from "../theme.js";
import invertTheme from "../invertTheme.js";

const Action = styled(Box)`
  text-decoration: none;
  opacity: 0;
  animation: institution-card-fade-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  will-change: opacity;

  @keyframes institution-card-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  * {
    text-decoration: none;

    &::selection {
      color: ${(props) =>
        props.theme.colors[props.cardColor] &&
        shade(0.9375, props.theme.colors[props.cardColor])};
      background-color: ${(props) => props.theme.colors.grey[0]};
    }
  }

  &:not(:last-child) {
    > * {
      margin-bottom: 32px;
    }
  }
`;

const Wrapper = styled(Box)`
  position: relative;
  background-color: ${(props) => props.theme.colors[props.cardColor]};
  &:not(:lang(ar)) {
    &:before,
    &:after {
      right: 0;
    }
    &:after {
      background: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to right, " +
          rgba(props.theme.colors[props.cardColor], 1) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"};
    }
  }
  &:lang(ar) {
    &:before,
    &:after {
      left: 0;
    }
    &:after {
      background: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to left, " +
          rgba(props.theme.colors[props.cardColor], 1) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"};
    }
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 0;
    width: 100%;
    max-width: 672px;
    height: 100%;
    filter: grayscale(1);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    /* Fallback */
    background-image: ${(props) =>
      "url(" + (props.backgroundImage ? props.backgroundImage[2] : "") + ")"};

    /* Webkit fallback */
    background-image: ${(props) =>
      "-webkit-image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    /* Regular imageset */
    background-image: ${(props) =>
      "image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    @media (min-width: 52em) {
      /* Fallback */
      background-image: ${(props) =>
        "url(" + (props.backgroundImage ? props.backgroundImage[1] : "") + ")"};

      /* Webkit fallback */
      background-image: ${(props) =>
        "-webkit-image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};

      /* Regular imageset */
      background-image: ${(props) =>
        "image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      background-image: ${(props) =>
        props.theme.colors[props.imageColor] &&
        "linear-gradient(to bottom, " +
          (props.imageColor
            ? rgba(props.theme.colors[props.imageColor], 0.66)
            : "") +
          ", " +
          (props.imageColor
            ? rgba(props.theme.colors[props.imageColor], 0.66)
            : "") +
          "), url(" +
          (props.backgroundImage ? props.backgroundImage[2] : "") +
          ")"};
    }
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 0;
    width: 100%;
    max-width: 672px;
    height: 100%;
  }
`;

const Content = styled(Box)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 640px;
`;

const CustomButton = styled(Button)`
  &:hover {
    color: ${(props) => props.theme.colors[props.cardColor]};
  }
  &:active {
    color: ${(props) => props.theme.colors[props.cardColor]};
  }
`;

const determineColor = function (type) {
  let color = "violet";
  if (
    type === "update" ||
    type === "institution" ||
    type === null ||
    type === undefined
  ) {
    color = "ash";
  }
  if (
    type === "middle-east-and-north-africa" ||
    type === "oriente-medio-y-norte-de-africa" ||
    type === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (type === "africa" || type === "africa1" || type === "افريقيا") {
    color = "green";
  }
  if (
    type === "latin-america" ||
    type === "america-latina-y-el-caribe" ||
    type === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (type === "asia" || type === "asia1" || type === "اسيا") {
    color = "pink";
  }
  if (
    type === "europe--central-asia" ||
    type === "europa-y-asia-central" ||
    type === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

const InstitutionCard = (props) => {
  if (props.object) {
    return (
      <ThemeProvider theme={invertTheme}>
        <Action
          className={props.className}
          style={props.style}
          cardColor={determineColor(props.object.type)}
        >
          <Wrapper
            px={4}
            py={5}
            backgroundImage={
              props.object.data.header_image.url
                ? [
                    props.object.data.header_image.half.url,
                    props.object.data.header_image.quarter.url,
                  ]
                : ""
            }
            cardColor={determineColor(props.object.type)}
          >
            <Content>
              <H2
                children={
                  props.object.data.title[0]
                    ? props.object.data.title[0].text
                    : ""
                }
                mb={5}
              />
              {/* <Text
                children={
                  props.object.data.description[0]
                    ? props.object.data.description[0].text
                    : "" ||
                      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."
                }
                mb={5}
              /> */}
              <CustomButton
                is={Link}
                to={
                  "/" + props.object.lang + "/institution/" + props.object.uid
                }
                children={
                  props.object.lang === "en-us"
                    ? "View projects"
                    : props.object.lang === "es-mx"
                    ? "Ver proyectos"
                    : "انظر المشاريع"
                }
                cardColor={determineColor(props.object.type)}
                aria-label={
                  props.object.data.title[0]
                    ? props.object.data.title[0].text
                    : ""
                }
              />
            </Content>
          </Wrapper>
        </Action>
      </ThemeProvider>
    );
  } else {
    return null;
  }
};

InstitutionCard.defaultProps = {
  title: "Institution Name",
  cardColor: "grey.0",
};

export default InstitutionCard;



// WEBPACK FOOTER //
// ./src/components/InstitutionCard.js